import { CombinedProvider } from './context';
import { ThemeContext } from './context/ThemeContext';
import { AuthContext } from './context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import './sass/App.scss';
import React, { useContext, useEffect, Suspense, lazy } from 'react';
import SideBar from './components/layout/SideBar';
import RightToolbar from './components/layout/RightToolbar';
import UserGuideDrawer from './components/layout/UserGuideDrawer';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SuperAdminRoute from './components/auth/SuperAdminRoute';
import RankRoute from './components/auth/RankRoute';
import DesktopOnly from './components/auth/DesktopOnly';
import MobileLayout from './components/layout/MobileLayout';
import useIsMobile from './hooks/useIsMobile';
import ErrorBoundary from './components/common/ErrorBoundary';
import UserHydrator from './components/auth/UserHydrator';
import SystemAlertBanner from './components/common/SystemAlertBanner';
import { getUserRole } from './utils/authUtils';
import { FaCompress } from 'react-icons/fa';

const DashboardPage = lazy(() => import('./components/home/DashboardPage'));
const HomeRedirect = lazy(() => import('./components/home/HomeRedirect'));
const ManageDashboards = lazy(() => import('./components/admin/ManageDashboards'));
const Templates = lazy(() => import('./components/templates/Templates'));
const Settings = lazy(() => import('./components/admin/Settings'));
const AdminApp = lazy(() => import('./components/admin/AdminApp'));
const EmailLog = lazy(() => import('./components/EmailLog'));
const ClientInsights = lazy(() => import('./components/client-insights/ClientInsights'));
const Login = lazy(() => import('./components/login/Login'));
const ForgotPassword = lazy(() => import('./components/login/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/login/ResetPassword'));
const ChangePassword = lazy(() => import('./components/login/ChangePassword'));
const TosAcceptance = lazy(() => import('./components/login/TosAcceptance'));

const RouteFallback = () => <div className="route-fallback" />;

const App = () => {
	return (
		<div className="App">
			<ErrorBoundary>
				<CombinedProvider>
					<UserHydrator>
						<SystemAlertBanner />
						<Router>
							<Suspense fallback={<RouteFallback />}>
								<Routes>
									<Route path="/login" element={<Login />} />
									<Route path="/forgot-password" element={<ForgotPassword />} />
									<Route path="/reset-password" element={<ResetPassword />} />
									<Route
										path="/change-password"
										element={
											<ProtectedRoute>
												<ChangePassword />
											</ProtectedRoute>
										}
									/>
									<Route
										path="/tos"
										element={
											<ProtectedRoute>
												<TosAcceptance />
											</ProtectedRoute>
										}
									/>
									<Route
										path="/admin/*"
										element={
											<ProtectedRoute>
												<SuperAdminRoute>
													<DesktopOnly>
														<AdminApp />
													</DesktopOnly>
												</SuperAdminRoute>
											</ProtectedRoute>
										}
									/>
									<Route
										path="/*"
										element={
											<ProtectedRoute>
												<MainApp />
											</ProtectedRoute>
										}
									/>
								</Routes>
							</Suspense>
						</Router>
					</UserHydrator>
				</CombinedProvider>
			</ErrorBoundary>
		</div>
	);
};

const MainApp = () => {
	const { state: authState } = useContext(AuthContext);
	const { state, actions: { toggleFullscreen, toggleUserGuide } } = useContext(ThemeContext);
	const isMobile = useIsMobile();

	// ?fullscreen=1 opens straight into fullscreen - lets a dedicated display boot to a dashboard URL.
	useEffect(() => {
		if (new URLSearchParams(window.location.search).get('fullscreen') === '1') {
			toggleFullscreen(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Escape key closes the user guide drawer (if open) or exits fullscreen.
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key !== 'Escape') return;
			if (state.userGuideOpen) {
				toggleUserGuide(false);
			} else if (state.fullscreen) {
				toggleFullscreen(false);
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [state.fullscreen, state.userGuideOpen, toggleFullscreen, toggleUserGuide]);

	// Only redirect once the user object is loaded to avoid false redirects during hydration
	if (authState.user && getUserRole(authState.user) === 'SuperAdmin') {
		return <Navigate to="/admin/dashboard" replace />;
	}
	// Phones get a monitor-only shell: Dashboard + Settings. Email Log, Client Insights and
	// everything else bounce to /home.
	if (isMobile) {
		return (
			<MobileLayout>
				<Suspense fallback={<RouteFallback />}>
					<Routes>
						<Route path="/home" element={<HomeRedirect/>} />
						<Route path="/dashboards/:dashboardId" element={<DashboardPage/>} />
						<Route path="/settings" element={<Settings/>} />
						<Route path="*" element={<Navigate to="/home" replace />} />
					</Routes>
				</Suspense>
			</MobileLayout>
		);
	}

	const sidebarWidth = state.sidebarCollapsed ? '56px' : '220px';
	const rightToolbarWidth = '44px';

	return (
		<div className="d-flex">
			{!state.fullscreen && <SideBar/>}
			{!state.fullscreen && (
				<UserGuideDrawer
					isOpen={state.userGuideOpen}
					sidebarWidth={sidebarWidth}
					onClose={() => toggleUserGuide(false)}
				/>
			)}
			<div
				className={`main-content flex-grow-1${state.fullscreen ? ' main-content--fullscreen' : ''}`}
				style={{
					marginLeft: state.fullscreen ? 0 : sidebarWidth,
					marginRight: state.fullscreen ? 0 : rightToolbarWidth,
					minHeight: '100vh',
					transition: 'margin-left 0.3s ease, margin-right 0.3s ease'
				}}
			>
				<Suspense fallback={<RouteFallback />}>
					<Routes>
						<Route path="/" element={<Navigate to="/home" replace />} />
						<Route path="/home" element={<HomeRedirect/>} />
						<Route path="/dashboards/:dashboardId" element={<DashboardPage/>} />
						<Route
							path="/manage-dashboards"
							element={<RankRoute minRank={3}><ManageDashboards/></RankRoute>}
						/>
						<Route
							path="/templates"
							element={<RankRoute minRank={3}><Templates/></RankRoute>}
						/>
						<Route path="/email-log" element={<RankRoute minRank={3}><EmailLog/></RankRoute>} />
						<Route path="/client-insights" element={<RankRoute minRank={3}><ClientInsights/></RankRoute>} />
						<Route path="/settings" element={<Settings/>} />
					</Routes>
				</Suspense>
			</div>
			{!state.fullscreen && <RightToolbar/>}
			{state.fullscreen && (
				<button
					className="fullscreen-exit-btn"
					onClick={() => toggleFullscreen(false)}
					title="Exit fullscreen"
				>
					<FaCompress />
				</button>
			)}
		</div>
	);
};

export { MainApp };
export default App;
